import { useEffect, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  projectBoardApi,
  useGetServiceOrdersQuery,
  useGetBoardCompaniesQuery,
  useCreateServiceOrderMutation,
} from 'app/services/projectBoardApi';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import { useTabAccess, useBoardCurrentUser } from '../../hooks/useProjectBoardData';
import ServiceOrderCard from './ServiceOrderCard';

/**
 * SERVICE TAB (`#service` — `service/view/service.js` + service.tmpl),
 * inventory §B7 / parity 193–198. Structural clone of the Shipping tab
 * (see ShippingTab) with the service endpoints, `ul#service-orders-list`,
 * the "Service Orders" heading and the FULL-MODEL save contract (the card).
 *
 * Caches shared with Shipping: the `'CompanyCollection'` session cache (RTK
 * getBoardCompanies) and the `'ShippingUserModel'` creator cache (the SAME
 * key space — orderShared claim set; inventory B10 "shared cache, real").
 *
 * Permission gating: same parity-191 ruling as Shipping — all cards
 * (including freshly created ones) gated by the tab access.
 *
 * REALTIME (parity 197): socket `put` on `ksdmprojectservice` merges into the
 * cached row. The legacy `instance.set` would also trip the model's
 * full-model autosave (echo write amplification) — not replicated
 * (3c/3d precedent).
 */

const SERVICE_DJANGO_MODEL = 'ksdmprojectservice';

const ServiceTab = ({ projectId }: { projectId: number }) => {
  const dispatch = useAppDispatch();
  const { data: orders } = useGetServiceOrdersQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: companies } = useGetBoardCompaniesQuery();
  const { data: me } = useBoardCurrentUser();
  const access = useTabAccess(projectId, 'service');
  const [createOrder] = useCreateServiceOrderMutation();

  const [newIds, setNewIds] = useState<ReadonlySet<number>>(() => new Set());

  useEffect(() => {
    if (!Number.isFinite(projectId)) return undefined;
    const socket = getSocket();
    const onData = (msg: SocketDataMessage) => {
      if (!msg || msg.model !== SERVICE_DJANGO_MODEL || msg.code !== 'put') return;
      const data = (msg.data ?? {}) as Record<string, unknown>;
      const id = Number((data as { id?: unknown }).id ?? msg.id);
      if (!Number.isFinite(id)) return;
      dispatch(
        projectBoardApi.util.updateQueryData('getServiceOrders', projectId, (draft) => {
          const row = draft.find((r) => r.id === id);
          if (row) Object.assign(row, data);
        })
      );
    };
    socket.on('data', onData);
    return () => {
      socket.off('data', onData);
    };
  }, [projectId, dispatch]);

  const onAddOrder = () => {
    if (!access) return;
    createOrder({ projectId })
      .unwrap()
      .then((row) => {
        setNewIds((prev) => new Set(prev).add(row.id));
      })
      .catch(() => undefined);
  };

  const ready = !!(orders && companies && me);

  return (
    <div className="content">
      <div className="content-item col-xs-12">
        <div className="col-xs-12">
          <div className="col-xs-12" style={{ height: '100%' }}>
            <div className="group-heading main-title clearfix">
              <h2 className="left">Service Orders</h2>
              <button
                id="add-order"
                className={`right btn-add${access ? '' : ' non-clickable'}`}
                tabIndex={-1}
                style={{ margin: 0, marginLeft: 'auto', position: 'unset', right: 0 }}
                onClick={onAddOrder}
              >
                <i
                  className="plus"
                  style={{ fontSize: 17, paddingRight: 9, position: 'relative', top: 1 }}
                >
                  +
                </i>
                Order
              </button>
            </div>
            <ul
              id="service-orders-list"
              className="service-orders-list prj-collapsible-list col-xs-12"
            >
              {ready &&
                orders!.map((order) => (
                  <ServiceOrderCard
                    key={order.id}
                    projectId={projectId}
                    order={order}
                    companies={companies!}
                    me={me!}
                    access={access}
                    // Live baseline: every fetched card renders EXPANDED
                    // (3e verify corrected parity 183).
                    initiallyExpanded
                    autoFocusTitle={newIds.has(order.id)}
                  />
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTab;
